import React from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button, FormControl, FormHelperText, styled, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import { Field, FieldAttributes, FieldProps } from 'formik';
import { useSortableList } from 'lib/sortable/useSortableList';
import { SortableItemRow, SortableFieldDefinition, FormComponentProps } from 'lib/sortable/SortableItemRow';
import { Row } from 'ui/Flex';
import { MuiModal } from 'ui/MuiModal';

export type { SortableFieldDefinition, FormComponentProps };

const PREFIX = 'SortableFormField';

const classes = {
    root: `${PREFIX}-root`
};

const StyledMuiModal = styled(MuiModal)(({ theme }) => ({
    minWidth: theme.spacing(48)
}));

interface SortableFormFieldProps<Model> {
    label?: React.ReactNode;
    description?: string;
    fields: SortableFieldDefinition[];
    FormComponent: React.FunctionComponent<FormComponentProps<Model>>;
}

export function SortableFormField<Model extends { index?: number }>(
    props: FieldAttributes<SortableFormFieldProps<Model>>
) {
    const { label, description, fields, FormComponent, ...fieldProps } = props;
    return (
        <Field {...fieldProps}>
            {(childProps: FieldProps<Model[]>) => (
                <SortableFormFieldComponent<Model>
                    label={label}
                    description={description}
                    fields={fields}
                    FormComponent={FormComponent}
                    {...childProps}
                />
            )}
        </Field>
    );
}

interface SortableFormFieldComponentProps<Model> extends FieldProps<Model[]>, SortableFormFieldProps<Model> {}

function SortableFormFieldComponent<Model extends { index?: number }>(
    props: SortableFormFieldComponentProps<Model>
) {
    const { field, meta, label, description, fields, FormComponent } = props;
    const value = React.useMemo(() => field.value || [], [field.value]);
    const isError = meta.touched && !!meta.error;

    const handleChange = React.useCallback(
        (newValue: Partial<Model>[]) => {
            field.onChange({ target: { value: newValue, name: field.name } });
        },
        [field]
    );

    const {
        items,
        openEdit,
        openAdd,
        currentItem,
        handleDragEnd,
        handleDeleteItem,
        handleSubmit,
        handleEditItem,
        handleAddItem,
        handleClose
    } = useSortableList<Model>({ value, onChange: handleChange });

    const renderValue = (item: Model, index: number) => (
        <SortableItemRow
            id={items[index]}
            fields={fields}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            key={items[index]}
            item={item}
            index={index}
        />
    );

    return (
        <FormControl fullWidth error={isError}>
            {label && (
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    {label}
                </Typography>
            )}
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={items} strategy={verticalListSortingStrategy}>
                    <Row flex={1} style={{ padding: 8 }}>
                        <div style={{ flexBasis: '5%', marginRight: '30px' }} />
                        {fields &&
                            fields.map(fieldDef => (
                                <Typography
                                    sx={{ fontSize: '0.75rem', flexBasis: fieldDef.width }}
                                    key={`sortable-title-${fieldDef.key}`}
                                >
                                    {fieldDef.label}
                                </Typography>
                            ))}
                        <div style={{ flexBasis: '10%' }} />
                    </Row>
                    {value && value.map(renderValue)}
                    <Button variant="outlined" onClick={handleAddItem}>
                        <Add />
                    </Button>
                </SortableContext>
            </DndContext>
            <StyledMuiModal className={classes.root} open={openEdit} onClose={handleClose}>
                <FormComponent
                    formTitle="Edit item"
                    close={handleClose}
                    value={currentItem}
                    onSubmit={handleSubmit}
                />
            </StyledMuiModal>
            <StyledMuiModal className={classes.root} open={openAdd} onClose={handleClose}>
                <FormComponent
                    formTitle="Add item"
                    close={handleClose}
                    value={currentItem}
                    onSubmit={handleSubmit}
                />
            </StyledMuiModal>
            {(isError || description) && (
                <FormHelperText sx={{ ml: 0 }} error={isError}>
                    {isError ? meta.error : description}
                </FormHelperText>
            )}
        </FormControl>
    );
}
